// src/components/AutoLoadPrompt.tsx
/**
 * T-22: Auto-Load Prompt Component
 * Banner that appears when a shared scene URL is detected
 */

import React from 'react';

interface AutoLoadPromptProps {
  sceneId: string | null;
  isLoading: boolean;
  error: string | null;
  onAutoLoad: () => void;
  onOpenLoadDialog: () => void;
  onDismiss: () => void;
}

export const AutoLoadPrompt: React.FC<AutoLoadPromptProps> = ({
  sceneId,
  isLoading,
  error,
  onAutoLoad,
  onOpenLoadDialog,
  onDismiss,
}) => {
  if (!sceneId) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-blue-600 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
            </div>
            
            <div className="min-w-0 flex-1">
              {!error ? (
                <div>
                  <p className="text-sm font-medium">
                    Shared scene detected
                  </p>
                  <p className="text-xs text-blue-100">
                    Scene ID: {sceneId}
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm font-medium">
                    Failed to load shared scene
                  </p>
                  <p className="text-xs text-blue-100">
                    {error}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {!error && !isLoading && (
              <>
                <button
                  onClick={onAutoLoad}
                  className="px-3 py-1 bg-blue-500 hover:bg-blue-400 text-white text-sm rounded-md transition-colors"
                >
                  Load Scene
                </button>
                
                <button
                  onClick={onOpenLoadDialog}
                  className="px-3 py-1 bg-blue-700 hover:bg-blue-600 text-white text-sm rounded-md transition-colors"
                >
                  Preview First
                </button>
              </>
            )}

            {isLoading && (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="text-sm">Loading...</span>
              </div>
            )}

            {error && (
              <button
                onClick={onOpenLoadDialog}
                className="px-3 py-1 bg-blue-500 hover:bg-blue-400 text-white text-sm rounded-md transition-colors"
              >
                Try Again
              </button>
            )}

            <button
              onClick={onDismiss}
              className="text-blue-100 hover:text-white transition-colors"
              aria-label="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};